import React, { useEffect, useRef, useState } from "react";
import { ConversationSummary } from "../lib/chatUtils";
import { cn } from "../lib/utils";
import { isToday, isYesterday, subDays, isAfter } from "date-fns";

interface Props {
  userConversations: ConversationSummary[];
  expandedGroups: { [key: string]: boolean };
  toggleGroup: (group: string) => void;
  selectedConversationId: string | null;
  onConversationClick: (conv: ConversationSummary) => void;
}

const ConversationGroupList: React.FC<Props> = ({
  userConversations,
  expandedGroups,
  toggleGroup,
  selectedConversationId,
  onConversationClick,
}) => {
  const [localExpandedGroups, setLocalExpandedGroups] = useState<{
    [key: string]: boolean;
  }>({
    Hoy: true,
    Ayer: false,
    "Últimos 7 días": false,
    "Últimos 30 días": false,
  });
  
  const hasInitializedRef = useRef(false);
  const prevUserConversationsLengthRef = useRef(0);

  const grouped = (() => {
    const groups: Record<string, ConversationSummary[]> = {
      Hoy: [],
      Ayer: [],
      "Últimos 7 días": [],
      "Últimos 30 días": [],
    };
    userConversations.forEach((conv) => {
      const date = new Date(conv.messages[0].dateSent!);
      if (isToday(date)) groups.Hoy.push(conv);
      else if (isYesterday(date)) groups.Ayer.push(conv);
      else if (isAfter(date, subDays(new Date(), 7))) groups["Últimos 7 días"].push(conv);
      else if (isAfter(date, subDays(new Date(), 30))) groups["Últimos 30 días"].push(conv);
    });
    return groups;
  })();

  useEffect(() => {
    if (prevUserConversationsLengthRef.current !== userConversations.length) {
      hasInitializedRef.current = false;
      prevUserConversationsLengthRef.current = userConversations.length;
    }
  }, [userConversations.length]);

  useEffect(() => {
    if (userConversations.length === 0) {
      hasInitializedRef.current = false;
      return;
    }

    if (hasInitializedRef.current) return;

    const groupOrder = ["Hoy", "Ayer", "Últimos 7 días", "Últimos 30 días"];
    
    const firstNonEmptyGroup = groupOrder.find((groupName) => grouped[groupName].length > 0);
    
    if (firstNonEmptyGroup) {
      const newExpandedGroups: { [key: string]: boolean } = {
        Hoy: true,
        Ayer: false,
        "Últimos 7 días": false,
        "Últimos 30 días": false,
      };
      
      if (firstNonEmptyGroup !== "Hoy") {
        newExpandedGroups[firstNonEmptyGroup] = true;
      }
      
      setLocalExpandedGroups(newExpandedGroups);
      hasInitializedRef.current = true;
    }
  }, [userConversations.length, grouped.Hoy.length, grouped.Ayer.length, grouped["Últimos 7 días"].length, grouped["Últimos 30 días"].length]);

  const handleToggleGroup = (group: string) => {
    if (group === "Hoy") return;
    setLocalExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  return (
    <>
      {Object.entries(grouped).map(([groupName, chats]) =>
        chats.length > 0 ? (
          <div key={groupName}>
            <div
              className={`flex px-2 py-1 justify-between items-center text-xs font-normal text-[#6F7D95] ${
                groupName !== "Hoy" ? "cursor-pointer" : ""
              }`}
              onClick={() => groupName !== "Hoy" && handleToggleGroup(groupName)}
            >
              <span>{groupName}</span>
              {groupName !== "Hoy" && <span>{localExpandedGroups[groupName] ? "−" : "+"}</span>}
            </div>
            {localExpandedGroups[groupName] && (
              <ul>
                {chats.map((conv) => (
                  <li
                    key={conv.id}
                    className={cn(
                      "text-sm p-2 hover:bg-[#F6F7F9] text-[#4A5364] cursor-pointer",
                      selectedConversationId === conv.id ? "bg-[#F6F7F9]" : ""
                    )}
                    onClick={() => onConversationClick(conv)}
                  >
                    {conv.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null
      )}
    </>
  );
};


export default ConversationGroupList;
